import axios, { AxiosInstance, AxiosResponse, AxiosError } from 'axios'
import { config } from '../config'
import { buildAuthUrl } from '../utils/url'

export interface ApiResponse<T = any> {
  success: boolean
  data: T
  message?: string
  error?: string
}

class ApiClient {
  private client: AxiosInstance;
  private baseURL: string;

  constructor() {
    // Use the existing config system that handles both dev and prod environments
    this.baseURL = config.apiUrl

    this.client = axios.create({
      baseURL: this.baseURL,
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      },
      withCredentials: true, // Important for cookies
    })

    // Add request interceptor to include Authorization header
    this.client.interceptors.request.use(
      (config) => {
        // Get access token from authService
        const accessToken = this.getAccessToken();

        if (accessToken && config.headers) {
          config.headers['Authorization'] = `Bearer ${accessToken}`;
        }

        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Add response interceptor
    this.client.interceptors.response.use(
      (response: AxiosResponse) => {
        return response.data
      },
      (error: AxiosError) => {
        // Handle 401 errors by redirecting to login with redirect_url
        if (error.response?.status === 401) {
          console.log('🔒 Authentication failed, redirecting to login...');

          // Clear any stored tokens and redirect to login with current URL
          if (typeof window !== 'undefined') {
            localStorage.removeItem('authTokens');
            this.redirectToLogin();
          }
        }

        // Handle other error types
        if (error.response?.data) {
          return Promise.reject(error.response.data)
        } else if (error.request) {
          return Promise.reject({
            success: false,
            error: 'Network error',
            message: 'Unable to connect to server'
          })
        } else {
          return Promise.reject({
            success: false,
            error: 'Request error',
            message: error.message
          })
        }
      }
    );
  }

  // Helper method to get access token from localStorage
  private getAccessToken(): string | null {
    const tokens = localStorage.getItem('authTokens');
    if (tokens) {
      try {
        const parsed = JSON.parse(tokens);
        return parsed.accessToken || null;
      } catch (e) {
        return null;
      }
    }
    return null;
  }

  // Helper method to redirect to login with current URL as redirect_url
  private redirectToLogin(): void {
    if (typeof window !== 'undefined') {
      const currentUrl = window.location.pathname + window.location.search;
      const loginUrl = buildAuthUrl('/auth/login', currentUrl);
      window.location.href = loginUrl;
    }
  }

  // Generic request methods
  async get(url: string, config?: any): Promise<any> {
    return this.client.get(url, config)
  }

  async post(url: string, data?: any, config?: any): Promise<any> {
    return this.client.post(url, data, config)
  }

  async put(url: string, data?: any, config?: any): Promise<any> {
    return this.client.put(url, data, config)
  }

  async delete(url: string, config?: any): Promise<any> {
    return this.client.delete(url, config)
  }

  async patch(url: string, data?: any, config?: any): Promise<any> {
    return this.client.patch(url, data, config)
  }

  // Base URL getter
  getBaseURL(): string {
    return this.baseURL;
  }

  // Public method to redirect unauthenticated users to login with current URL
  public redirectToLoginWithCurrentUrl(): void {
    this.redirectToLogin();
  }
}

// Create and export the API client instance
export const api = new ApiClient()
export default api

// Utility function for components to manually redirect to login with current URL
export const redirectToLogin = (customUrl?: string): void => {
  if (typeof window !== 'undefined') {
    const currentUrl = customUrl || (window.location.pathname + window.location.search);
    const loginUrl = buildAuthUrl('/auth/login', currentUrl);
    window.location.href = loginUrl;
  }
}